import { lazy, useEffect, Suspense } from 'react'
import { createBrowserRouter, useNavigate, useLocation } from 'react-router-dom'
import Layout from './components/layout/index'
import RouteGuard from './features/auth/components/RouteGuard'
import { GlobalLoading } from './components/common/GlobalLoading'
import ErrorBoundary from './components/common/ErrorBoundary'

// Lazy load pages
const WorkflowList = lazy(() => import('./features/workflow/pages/list'))
const WorkflowEditor = lazy(() => import('./features/workflow/pages/editor'))
const RunLogPage = lazy(() => import('./features/runlog/pages/list'))
const MossChatList = lazy(() => import('./features/moss-chat/pages/list'))
const WorkflowChat = lazy(() => import('./features/moss-chat/pages/workflow-chat'))
const Login = lazy(() => import('./features/auth/pages/Login'))
const Register = lazy(() => import('./features/auth/pages/Register'))
const Welcome = lazy(() => import('./features/auth/pages/Welcome'))
const Callback = lazy(() => import('./features/auth/pages/Callback')) // OIDC回调页面
const EnvDebug = lazy(() => import('./features/auth/pages/EnvDebug')) // 环境配置验证页面
const VoiceCallLogs = lazy(() => import('./features/moss-chat-signalr/pages/VoiceCallLogs'))
const ImageChat = lazy(() => import('./features/image-chat/pages/index'))
const ImageGallery = lazy(() => import('./features/image-chat/pages/gallery'))

// 如果URL中不是OIDC回调但包含code参数，可能是API令牌
const isCodeGrantRedirect = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has("code") && !window.location.pathname.includes("/callback");
};

// 重定向组件，用于首页重定向
const RedirectComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // 检查是否有保存的returnUrl
  const returnUrl = sessionStorage.getItem('returnUrl');

  useEffect(() => {
    // 如果是OIDC回调重定向，不处理
    if (isCodeGrantRedirect()) {
      return;
    }

    // 优先使用保存的returnUrl进行重定向
    if (returnUrl && returnUrl !== '/') {

      sessionStorage.removeItem('returnUrl');
      navigate(returnUrl, { replace: true });
      return;
    }

    // 默认重定向到 Welcome 页面（Landing Page）
    if (location.pathname === '/') {
      navigate('/welcome', { replace: true });
    }
  }, [navigate, location, returnUrl]);

  return null;
};

// 修改路由配置，添加RouteGuard组件
export const router = createBrowserRouter([
  // 根路径重定向
  {
    path: '/',
    element: <RedirectComponent />,
    errorElement: <ErrorBoundary />,
  },

  // 认证回调路由
  {
    path: '/callback',
    element: (
      <Suspense fallback={<GlobalLoading />}>
        <Callback />
      </Suspense>
    ),
  },
  // ✅ P0-1 修复：添加大小写别名，兼容 .env 中的 /callBack 配置
  // 生产环境 Nginx 默认大小写敏感，需要两个路由都能匹配
  {
    path: '/callBack',
    element: (
      <Suspense fallback={<GlobalLoading />}>
        <Callback />
      </Suspense>
    ),
  },

  // 登录页面
  {
    path: '/login',
    element: (
      <Suspense fallback={<GlobalLoading />}>
        <Login />
      </Suspense>
    ),
  },
  // 注册页面
  {
    path: '/register',
    element: (
      <Suspense fallback={<GlobalLoading />}>
        <Register />
      </Suspense>
    ),
  },

  // 欢迎页
  {
    path: '/welcome',
    element: (
      <Suspense fallback={<GlobalLoading />}>
        <Welcome />
      </Suspense>
    ),
    errorElement: <ErrorBoundary />,
  },

  // 环境配置验证页面（仅开发环境）
  {
    path: '/env-debug',
    element: (
      <Suspense fallback={<GlobalLoading />}>
        <EnvDebug />
      </Suspense>
    ),
  },

  // 应用主体路由,需要认证，使用主布局
  {
    path: '/app',
    element: <Layout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <RouteGuard />, // 使用RouteGuard包装所有需要认证的路由
        children: [
          // 工作流模块
          {
            path: 'workflow',
            element: (
              <Suspense fallback={<GlobalLoading />}>
                <WorkflowList />
              </Suspense>
            ),
          },
          {
            path: 'workflow/edit/:id',
            element: (
              <Suspense fallback={<GlobalLoading />}>
                <WorkflowEditor />
              </Suspense>
            ),
          },
          {
            path: 'workflow/:id',
            element: (
              <Suspense fallback={<GlobalLoading />}>
                <WorkflowEditor />
              </Suspense>
            ),
          },
          {
            path: 'workflow/create',
            element: (
              <Suspense fallback={<GlobalLoading />}>
                <WorkflowEditor />
              </Suspense>
            ),
          },

          // 运行日志模块
          {
            path: 'runlog',
            element: (
              <Suspense fallback={<GlobalLoading />}>
                <RunLogPage />
              </Suspense>
            ),
          },

          // Vinka对话模块
          {
            path: 'moss-chat',
            element: (
              <Suspense fallback={<GlobalLoading />}>
                <MossChatList />
              </Suspense>
            ),
          },
          {
            path: 'moss-chat/workflow/:id',
            element: (
              <Suspense fallback={<GlobalLoading />}>
                <WorkflowChat />
              </Suspense>
            ),
          },
          {
            path: 'voice-logs',
            element: (
              <Suspense fallback={<GlobalLoading />}>
                <VoiceCallLogs />
              </Suspense>
            ),
          },

          // 图片生成模块
          {
            path: 'image-chat',
            element: (
              <Suspense fallback={<GlobalLoading />}>
                <ImageChat />
              </Suspense>
            ),
          },
          {
            path: 'image-chat/gallery',
            element: (
              <Suspense fallback={<GlobalLoading />}>
                <ImageGallery />
              </Suspense>
            ),
          },
          // 没有匹配到路由时，重定向到工作流列表
          {
            path: '*',
            element: <RedirectComponent />,
          },
        ],
      },
    ],
  },
]) 
